import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';
import { Button, IconMain, ImgMain } from '#components';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { toShortAddress, useEventModalsStore, useProviderConfig } from '#imports';
import { useUserStore } from '~/stores/user/useUserStore';
import type { Address, Hash } from 'viem';
import type { EvmFullFillCreateOrderListing } from '~/service/api/types';
import type { EvmConsideration, EvmItemType } from '~/service/EVM/types';

dayjs.extend(duration);

const columnHelper = createColumnHelper<EvmFullFillCreateOrderListing>();

export function useOfferColumns({ onSort, onShowModal, ownedCount }: {
    onSort: (param: string) => void;
    onShowModal: (data: Pick<EvmFullFillCreateOrderListing, 'order' | 'price' | 'source' | 'nftInfo' | 'orderHash' | 'tokensRemaining'>) => void;
    ownedCount: number;
}) {
    const userStore = useUserStore();

    return [
        columnHelper.accessor('price.amount', {
            header: () => h(
                'div',
                {
                    class: 'display-flex flex-nowrap cursor-pointer',
                    onClick: () => onSort('price.priceUSD')
                },
                [
                    'Price',
                    h(
                        IconMain,
                        {
                            size: 16,
                            icon: 'up-down'
                        }
                    )
                ]
            ),
            cell: (info) => {
                const { row } = info;
                return h('div', { class: 'display-flex align-items-center gap-4' }, [
                    h(ImgMain, {
                        src: info.row.original.price?.logo,
                        alt: info.row.original.nftInfo.name,
                        quality: '90',
                        densities: 'x1 x2',
                        class: 'logo',
                        width: 24,
                        height: 24,
                        size: '24px',
                        loading: 'lazy'
                    }),
                    h('span', { class: 'compact-400-accent-numeric' }, row.original.price?.amount)
                ]);
            }
        }),
        // columnHelper.accessor('order.value', {
        //     header: 'Floor diff',
        //     cell: info =>
        //         h('span', { class: 'compact-400-accent-numeric text-color-brand-mojito' }, `${info.row.original.order.value}%`)
        // }),
        columnHelper.accessor('order.parameters.consideration', {
            header: 'Quantity',
            cell: info =>
                h('span', { class: 'compact-400-accent' }, calcQuantity(info.row.original.order.parameters.consideration, info.row.original.order.parameters.offerer))
        }),
        columnHelper.accessor('order.parameters.endTime', {
            header: () => h(
                'div',
                {
                    class: 'display-flex flex-nowrap cursor-pointer',
                    onClick: () => onSort('endTime')
                },
                [
                    'Expiration',
                    h(
                        IconMain,
                        {
                            size: 16,
                            icon: 'up-down'
                        }
                    )
                ]
            ),
            cell: info =>
                h('span', { class: 'compact-400-accent' }, getTimeLeft(String(info.row.original.order.parameters.endTime)))
        }),
        columnHelper.accessor('order.parameters.offerer', {
            header: 'From',
            cell: info =>
                h('span', { class: 'compact-400-accent text-color-brand-aviation' }, toShortAddress(info.row.original.order.parameters.offerer))
        }),
        columnHelper.accessor('order.parameters.offerer', {
            header: '',
            cell: (info) => {
                if (userStore.user && info.row.original.orderHash && info.row.original.order.parameters.offerer === userStore.user) {
                    return h(OfferCancelButton, {
                        fullOffer: info.row.original
                    });
                }
                else if (ownedCount && userStore.user) {
                    return h(OfferRespondButton, {
                        orderHash: info.row.original.orderHash,
                        onShowModal: () => onShowModal({
                            price: info.row.original.price,
                            source: info.row.original.source,
                            tokensRemaining: ownedCount < info.row.original.tokensRemaining ? ownedCount : info.row.original.tokensRemaining,
                            nftInfo: info.row.original.nftInfo,
                            order: info.row.original.order,
                            orderHash: info.row.original.orderHash
                        })
                    });
                }
            }
        })
    ];
}

const OfferRespondButton = defineComponent({
    props: {
        orderHash: {
            type: String as PropType<Hash>,
            required: true
        }
    },
    emits: ['showModal'],
    setup(props, { emit }) {
        return () =>
            h(
                Button,
                {
                    color: 'bg.secondary',
                    size: 'size.small',
                    class: 'text-center',
                    style: { minHeight: '39px' },
                    onClick: () => emit('showModal')
                },
                { default: () => h('span', {}, 'Respond') }
            );
    }
});

const OfferCancelButton = defineComponent({
    props: {
        fullOffer: {
            type: Object as PropType<EvmFullFillCreateOrderListing>,
            required: true
        }
    },
    setup(props) {
        const { offer } = useEVMApi();
        const evmCore = useEVMCore();

        const {
            utils: { isEvmErc1155, isEvmErc721 }
        } = useProviderConfig()(['utils']);

        const modalEventsStore = useEventModalsStore();

        const getDescriptionForModalCancelOffer = (itemType: EvmItemType) => {
            let result: string[];
            result = [];
            if (isEvmErc721(itemType)) {
                result = [];
            }
            else if (isEvmErc1155(itemType)) {
                result = [`${calcQuantity(props.fullOffer.order.parameters.consideration, props.fullOffer.order.parameters.offerer)} items`];
            }

            return result;
        };

        const sendCancel = async () => {
            modalEventsStore.add(
                {
                    hash: props.fullOffer.orderHash,
                    type: 'cancel_offer',
                    status: 'success',
                    images: [`${props.fullOffer.nftInfo.displayImageURL ? props.fullOffer.nftInfo.displayImageURL : ''}`],
                    imageState: 'default',
                    title: props.fullOffer.nftInfo.name,
                    description: getDescriptionForModalCancelOffer(props.fullOffer.nftInfo.itemType),
                    heading: 'Cancel this offer?',
                    itemType: props.fullOffer.nftInfo.itemType,
                    content: `This will cancel your offer for ${calcQuantity(props.fullOffer.order.parameters.consideration, props.fullOffer.order.parameters.offerer)} ${props.fullOffer.nftInfo.name} items for ${props.fullOffer.price.amount} ${props.fullOffer.price.symbol}`,
                    button: {
                        callback: async () => {
                            await evmCore.eventEmitter.emit(
                                'sendCancel',
                                async () => {
                                    if (!props.fullOffer.orderHash) {
                                        throw new Error('No orderHash provided');
                                    }

                                    try {
                                        await offer.cancelOffer(props.fullOffer.orderHash);
                                        modalEventsStore.removeByType('cancel_offer');
                                    }
                                    catch (e) {
                                        throw {
                                            originalError: e,
                                            modalInfo: {
                                                images: [`${props.fullOffer.nftInfo.displayImageURL ? props.fullOffer.nftInfo.displayImageURL : ''}`],
                                                description: getDescriptionForModalCancelOffer(props.fullOffer.nftInfo.itemType),
                                                itemType: props.fullOffer.nftInfo.itemType,
                                                title: props.fullOffer.nftInfo.name
                                            }
                                        };
                                    }
                                },
                                { isCancel: true, modalInfo: {
                                    images: [`${props.fullOffer.nftInfo.displayImageURL ? props.fullOffer.nftInfo.displayImageURL : ''}`],
                                    description: getDescriptionForModalCancelOffer(props.fullOffer.nftInfo.itemType),
                                    itemType: props.fullOffer.nftInfo.itemType,
                                    title: props.fullOffer.nftInfo.name,
                                    tokenLink: `${props.fullOffer.nftInfo.collection.slug}/${props.fullOffer.nftInfo.identifier}`
                                }, collectionName: props.fullOffer.nftInfo.collection.name }
                            );
                        },
                        title: 'Confirm'
                    }
                });
        };

        return () =>
            h(
                Button,
                {
                    color: 'bg.secondary',
                    size: 'size.small',
                    style: { minHeight: '39px' },
                    class: 'text-center',
                    onClick: sendCancel
                },
                { default: () => h('span', {}, 'Cancel') }
            );
    }
});

function calcQuantity(considerations: EvmConsideration[], offerer: Address) {
    return considerations.filter(filterItem => filterItem.recipient === offerer).reduce((sum, item) => sum + Number(item.endAmount), 0);
}

function getTimeLeft(dateString: string): string {
    const now = dayjs();
    const target = dayjs.unix(Number(dateString));
    const diff = target.diff(now);
    if (diff <= 0) return 'time is up';

    const timeLeft = dayjs.duration(diff);
    if (timeLeft.asDays() >= 1) {
        return `${Math.floor(timeLeft.asDays())} d`;
    }
    else if (timeLeft.asHours() >= 1) {
        return `${Math.floor(timeLeft.asHours())} h`;
    }
    else {
        return `${Math.floor(timeLeft.asMinutes())} min`;
    }
}
